// ReportDrawer — slide-in right panel that holds the queue of insights
// the user has added via "Add to PDF report" buttons. Visible only when
// at least one item is queued. Clicking Generate POSTs the queue to the
// middleware and downloads the resulting PDF.

import { useState } from "react";
import { generateReport } from "./api";
import { useReport, type ReportItem } from "./ReportContext";

interface Props {
  bearer: string;
  organization: string;
  threadId: number | null;
}

export function ReportDrawer({ bearer, organization, threadId }: Props) {
  const { items, remove, move, setTitle, clear } = useReport();
  const [open, setOpen] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (items.length === 0) return null;

  const onGenerate = async () => {
    setGenerating(true);
    setError(null);
    try {
      const blob = await generateReport(bearer, { organization, thread_id: threadId, items });
      // Trigger browser download. Object URL is revoked after the click
      // resolves so we don't leak the blob.
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `agami-report-${new Date().toISOString().slice(0, 10)}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setGenerating(false);
    }
  };

  return (
    <>
      {/* Collapsed pill — sits flush right when the drawer is closed. */}
      {!open && (
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="fixed right-4 bottom-6 z-40 inline-flex items-center gap-2 rounded-full bg-brand text-white px-4 h-11 shadow-lg hover:bg-brand-600 transition-colors text-[13px] font-medium"
          aria-label="Open report drawer"
        >
          <ReportIcon />
          <span>Report</span>
          <span className="inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-white/20 text-[11px] font-semibold">
            {items.length}
          </span>
        </button>
      )}

      {open && (
        <aside
          className="fixed right-0 top-[70px] bottom-0 z-40 w-[360px] bg-white border-l border-line shadow-xl flex flex-col"
          aria-label="PDF report builder"
        >
          <header className="flex items-center gap-2 px-5 py-3 border-b border-line">
            <ReportIcon />
            <div className="text-[14px] font-semibold text-neutral-900">PDF report</div>
            <span className="text-[12px] text-neutral-500">{items.length} item{items.length === 1 ? "" : "s"}</span>
            <div className="ml-auto flex items-center gap-1">
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="h-7 w-7 inline-flex items-center justify-center rounded-full text-neutral-500 hover:bg-chip hover:text-neutral-800 transition-colors"
                aria-label="Hide drawer"
                title="Hide drawer"
              >
                <CloseIcon />
              </button>
            </div>
          </header>

          <div className="flex-1 min-h-0 overflow-y-auto px-3 py-3 space-y-2">
            {items.map((item, idx) => (
              <ItemRow
                key={item.id}
                item={item}
                index={idx}
                count={items.length}
                onTitle={(t) => setTitle(item.id, t)}
                onUp={() => move(item.id, -1)}
                onDown={() => move(item.id, 1)}
                onRemove={() => remove(item.id)}
              />
            ))}
          </div>

          <footer className="border-t border-line p-3 space-y-2">
            {error && (
              <div className="text-[12px] text-red-600 px-1">{error}</div>
            )}
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => clear()}
                disabled={generating}
                className="text-[12px] text-neutral-500 hover:text-neutral-800 px-2 h-9"
              >
                Clear all
              </button>
              <button
                type="button"
                onClick={onGenerate}
                disabled={generating}
                className="ml-auto inline-flex items-center gap-2 h-9 px-4 rounded-full bg-brand text-white text-[13px] font-medium hover:bg-brand-600 disabled:opacity-60 transition-colors"
              >
                {generating ? <Spinner /> : <DownloadIcon />}
                {generating ? "Generating…" : "Generate PDF"}
              </button>
            </div>
          </footer>
        </aside>
      )}
    </>
  );
}

interface RowProps {
  item: ReportItem;
  index: number;
  count: number;
  onTitle: (t: string) => void;
  onUp: () => void;
  onDown: () => void;
  onRemove: () => void;
}

function ItemRow({ item, index, count, onTitle, onUp, onDown, onRemove }: RowProps) {
  return (
    <div className="rounded-[12px] border border-line bg-white p-2.5 space-y-2">
      <div className="flex items-start gap-2">
        <span
          className={
            "shrink-0 mt-1 inline-flex h-4 w-4 items-center justify-center rounded-full text-[10px] font-semibold " +
            (item.kind === "chart"
              ? "bg-indigo-50 text-indigo-600"
              : "bg-emerald-50 text-emerald-600")
          }
          title={item.kind}
          aria-hidden
        >
          {item.kind === "chart" ? "C" : "A"}
        </span>
        <input
          value={item.title}
          onChange={(e) => onTitle(e.target.value)}
          className="flex-1 min-w-0 bg-transparent text-[13px] font-medium text-neutral-900 focus:outline-none focus:ring-1 focus:ring-brand rounded px-1 -mx-1"
          aria-label="Insight title"
        />
        <div className="flex items-center gap-0.5 text-neutral-500 shrink-0">
          <IconBtn label="Move up" onClick={onUp} disabled={index === 0}>
            <ChevronUp />
          </IconBtn>
          <IconBtn label="Move down" onClick={onDown} disabled={index === count - 1}>
            <ChevronDown />
          </IconBtn>
          <IconBtn label="Remove" onClick={onRemove}>
            <TrashIcon />
          </IconBtn>
        </div>
      </div>
      {item.kind === "chart" ? (
        <div className="rounded-[8px] border border-line overflow-hidden bg-neutral-50">
          <img src={item.imagePng} alt={item.title} className="w-full h-auto block" />
        </div>
      ) : (
        <div className="text-[12px] text-neutral-600 line-clamp-3 whitespace-pre-wrap">
          {item.text}
        </div>
      )}
      <div className="text-[11px] text-neutral-400 truncate">{item.userQuery}</div>
    </div>
  );
}

function IconBtn({
  label,
  onClick,
  disabled,
  children,
}: {
  label: string;
  onClick: () => void;
  disabled?: boolean;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className="h-6 w-6 inline-flex items-center justify-center rounded-full hover:bg-chip hover:text-neutral-800 transition-colors disabled:opacity-30 disabled:hover:bg-transparent"
      aria-label={label}
      title={label}
    >
      {children}
    </button>
  );
}

function ReportIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" className="h-4 w-4">
      <path strokeLinecap="round" strokeLinejoin="round" d="M7 4h7l4 4v12a1 1 0 01-1 1H7a1 1 0 01-1-1V5a1 1 0 011-1z" />
      <path strokeLinecap="round" strokeLinejoin="round" d="M14 4v4h4M9 13h6M9 17h6" />
    </svg>
  );
}

function DownloadIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" className="h-4 w-4">
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v12m0 0l-4-4m4 4l4-4M5 20h14" />
    </svg>
  );
}

function CloseIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" className="h-4 w-4">
      <path strokeLinecap="round" strokeLinejoin="round" d="M6 6l12 12M18 6L6 18" />
    </svg>
  );
}

function TrashIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" className="h-3.5 w-3.5">
      <path strokeLinecap="round" strokeLinejoin="round" d="M4 7h16M10 11v6M14 11v6M6 7l1 13a2 2 0 002 2h6a2 2 0 002-2l1-13M9 7V4a1 1 0 011-1h4a1 1 0 011 1v3" />
    </svg>
  );
}

function ChevronUp() {
  return (
    <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.75" className="h-3.5 w-3.5">
      <path strokeLinecap="round" strokeLinejoin="round" d="M4 10l4-4 4 4" />
    </svg>
  );
}

function ChevronDown() {
  return (
    <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.75" className="h-3.5 w-3.5">
      <path strokeLinecap="round" strokeLinejoin="round" d="M4 6l4 4 4-4" />
    </svg>
  );
}

function Spinner() {
  return (
    <svg viewBox="0 0 24 24" className="h-4 w-4 animate-spin" fill="none" stroke="currentColor" strokeWidth="2">
      <circle cx="12" cy="12" r="9" strokeOpacity="0.25" />
      <path strokeLinecap="round" d="M21 12a9 9 0 00-9-9" />
    </svg>
  );
}
